import { Controller, RegisterOptions, UseFormRegister } from 'react-hook-form';
import ReactCurrencyInputField from 'react-currency-input-field';
import clsx from 'clsx';

import { INPUT_TYPES } from '../../../constants';
import { LINK_REGEX } from '../../../utils/regex';
import {
  Input,
  Space,
  Form,
  Tag,
  Select,
  InputNumber,
  Checkbox,
  Tooltip,
} from 'antd';
import { InfoCircleFilled, QuestionCircleTwoTone } from '@ant-design/icons';

import './InputField.css';
import TierSelectField from './TierSelectField';

const { Option } = Select;

const warningPositions = {
  left: 'justify-start',
  center: 'justify-center',
  right: 'justify-end',
};

type InputElementProps = {
  name: string;
  type?: string;
  defaultValue?: any;
  rules?: RegisterOptions;
  label?: string;
  options?: any;
  errors?: any;
  styles?: React.CSSProperties;
  register?: UseFormRegister<any>;
  control: any;
  errorMessage?: string;
  tag?: string;
  selectOptions?: any;
  inputContainerStyles?: string;
  children?: React.ReactNode;
  spaceBetween?: boolean;
  disabled?: boolean;
  decorator?: string;
  className?: string;
  tooltip?: string;
  warning?: string;
  warningPosition?: 'left' | 'center' | 'right';
};

function lowercaseFirstLetter(str: string) {
  if (!str) {
    return str;
  }
  return str.charAt(0).toLowerCase() + str.slice(1);
}

const InputField = ({
  type = 'text',
  label,
  name,
  rules,
  errors,
  control,
  defaultValue = '',
  tag,
  selectOptions,
  disabled,
  spaceBetween,
  children,
  decorator,
  className,
  tooltip,
  warning,
  warningPosition = 'right',
}: InputElementProps) => {
  const renderErrorMessage = () => {
    const error = errors?.[name];
    if (!error) return null;

    switch (error.type) {
      case 'required':
        return error.message || 'This field is required';
      case 'pattern':
        return error.message || 'Invalid pattern';
      case 'minLength':
        return error.message || 'Value is too short';
      default:
        return 'Invalid value';
    }
  };

  const renderInputText = () => {
    return (
      <Space
        direction="vertical"
        size={4}
        className={clsx('relative w-full my-2.5 font-montserrat', className)}
      >
        {tooltip && (
          <div className="absolute -top-5 right-0.5">
            <Tooltip title={tooltip}>
              <QuestionCircleTwoTone />
            </Tooltip>
          </div>
        )}
        <div
          className={clsx('w-full flex items-center gap-4', {
            'justify-between': spaceBetween,
          })}
        >
          <label
            htmlFor={name}
            className="font-semibold text-primary-100 text-[14px]"
          >
            {label}
          </label>
          <div
            className={clsx('relative flex', {
              'flex-1': !spaceBetween,
            })}
          >
            <Controller
              control={control}
              name={name}
              rules={rules}
              defaultValue={defaultValue}
              render={({ field }) => (
                <Input
                  {...field}
                  id={name}
                  type={type}
                  placeholder={label}
                  disabled={disabled}
                  status={errors?.[name] ? 'error' : ''}
                  style={{ width: '256px' }}
                />
              )}
            />
            {children}
          </div>
        </div>
        {errors?.[name] && (
          <span className="flex items-center justify-end text-danger text-sm">
            {renderErrorMessage()}
          </span>
        )}
      </Space>
    );
  };

  const renderInputNumber = () => (
    <Space
      direction="vertical"
      size={4}
      className={clsx('relative w-full my-2.5 font-montserrat', className)}
    >
      {tooltip && (
        <div className="absolute -top-5 right-0.5">
          <Tooltip title={tooltip}>
            <QuestionCircleTwoTone />
          </Tooltip>
        </div>
      )}
      <div
        className={clsx('w-full flex items-center gap-4', {
          'justify-between': spaceBetween,
        })}
      >
        <label
          htmlFor={name}
          className="font-semibold text-primary-100 text-[14px]"
        >
          {label}
        </label>
        <div
          className={clsx('relative flex', {
            'flex-1': !spaceBetween,
          })}
        >
          <Controller
            control={control}
            name={name}
            rules={{
              ...rules,
              pattern: { value: /^[0-9]*$/, message: 'Invalid number' },
            }}
            render={({ field }) => (
              <InputNumber
                {...field}
                id={name}
                placeholder={label}
                disabled={disabled}
                status={errors?.[name] ? 'error' : ''}
                className={clsx('no-arrows', {
                  'text-danger': errors?.[name],
                })}
                style={{ width: '256px' }}
                onChange={(value) => field.onChange(value)}
                addonAfter={decorator}
              />
            )}
          />
        </div>
      </div>
      {errors?.[name] && (
        <span className="flex items-center justify-end text-danger text-sm">
          {renderErrorMessage()}
        </span>
      )}
    </Space>
  );

  const renderInputEmail = () => (
    <Space
      direction="vertical"
      size={4}
      className={clsx('relative w-full my-2.5 font-montserrat', className)}
    >
      {tooltip && (
        <div className="absolute -top-5 right-0.5">
          <Tooltip title={tooltip}>
            <QuestionCircleTwoTone />
          </Tooltip>
        </div>
      )}
      <div
        className={clsx('w-full flex items-center gap-4', {
          'justify-between': spaceBetween,
        })}
      >
        <label
          htmlFor={name}
          className="font-semibold text-primary-100 text-[14px]"
        >
          {label}
        </label>
        <div
          className={clsx('relative flex', {
            'flex-1': !spaceBetween,
          })}
        >
          <Controller
            control={control}
            name={name}
            rules={rules}
            render={({ field }) => (
              <Input
                {...field}
                id={name}
                placeholder={label}
                disabled={disabled}
                type="email"
                status={errors?.[name] ? 'error' : ''}
                style={{ width: '256px' }}
              />
            )}
          />
        </div>
      </div>
      {errors?.[name] && (
        <span className="flex items-center justify-end text-danger text-sm">
          {renderErrorMessage()}
        </span>
      )}
    </Space>
  );

  const renderInputSelect = () => {
    return (
      <Space
        direction="vertical"
        size={4}
        className={clsx('relative w-full my-2.5 font-montserrat', className)}
      >
        <div
          className={clsx('w-full flex items-center gap-4', {
            'justify-between': spaceBetween,
          })}
        >
          <div className="flex items-center gap-2">
            <label
              htmlFor={name}
              className="font-semibold text-primary-100 text-[14px]"
            >
              {label}
            </label>
            {tooltip && (
              <Tooltip title={tooltip}>
                <InfoCircleFilled className="text-[#293241]" />
              </Tooltip>
            )}
          </div>
          <div
            className={clsx('relative flex', {
              'flex-1': !spaceBetween,
            })}
          >
            <Controller
              control={control}
              name={name}
              rules={rules}
              defaultValue={defaultValue}
              render={({ field }) => (
                <Select
                  {...field}
                  id={name}
                  disabled={disabled}
                  status={errors?.[name] ? 'error' : ''}
                  className={clsx('w-64 max-h-7', {
                    'border-danger': errors?.[name],
                  })}
                  defaultValue={defaultValue}
                  onChange={(value) => field.onChange(value)}
                >
                  <Option value="" disabled>
                    Select an option
                  </Option>
                  {selectOptions.map((option: any, index: number) => (
                    <Option key={index} value={option.value}>
                      {option.label}
                    </Option>
                  ))}
                </Select>
              )}
            />
          </div>
        </div>
        {errors?.[name] && (
          <span className="flex items-center justify-end text-danger text-sm">
            {renderErrorMessage()}
          </span>
        )}
      </Space>
    );
  };

  const renderInputTextArea = () => (
    <Space
      direction="vertical"
      size={4}
      className={clsx('relative w-full my-2.5 font-montserrat', className)}
    >
      {tooltip && (
        <div className="absolute -top-5 right-0.5">
          <Tooltip title={tooltip}>
            <QuestionCircleTwoTone />
          </Tooltip>
        </div>
      )}
      <label
        htmlFor={name}
        className="font-semibold text-primary-100 text-[14px]"
      >
        {label}
      </label>
      <Controller
        control={control}
        name={name}
        rules={rules}
        render={({ field }) => (
          <Input.TextArea
            {...field}
            id={name}
            rows={6}
            placeholder={label}
            disabled={disabled}
            className={clsx('w-full h-28', {
              'border-danger focus:border-danger bg-danger/5': errors?.[name],
            })}
          />
        )}
      />
      {errors?.[name] && (
        <span className="flex items-center justify-end text-danger text-sm">
          {renderErrorMessage()}
        </span>
      )}
    </Space>
  );

  const renderInputMoney = () => {
    const currencyRules = {
      ...rules,
      validate: {
        notNegative: (value: number) =>
          value >= 0 || 'Value cannot be negative',
      },
      pattern: {
        value: /^\d+(\.\d{0,2})?$/,
        message: 'Only numbers allowed',
      },
    };

    const allRules = { ...rules, ...currencyRules };

    return (
      <>
        <div
          className={clsx(
            'relative w-full font-montserrat inline-flex items-center my-2.5 gap-4',
            {
              'justify-between': spaceBetween,
            },
            className,
          )}
        >
          {tooltip && (
            <div className="absolute -top-5 right-0.5">
              <Tooltip title={tooltip}>
                <QuestionCircleTwoTone />
              </Tooltip>
            </div>
          )}
          <label
            className="font-semibold text-primary-100 text-[14px]"
            htmlFor={name}
          >
            {label}
          </label>
          <Controller
            name={name}
            control={control}
            rules={allRules}
            render={({ field }) => (
              <div
                className={clsx(
                  'w-64 border rounded-md px-2 flex items-center',
                  {
                    'border-[#ff7875] focus:border-[#ff7875] hover:border-[#ffa39e]':
                      errors?.[name],
                    'border-[#CCCCCC] focus:border-[#6ea3ff] active:border-[#6ea3ff] hover:border-[#6ea3ff] focus-within:border-[#6ea3ff]':
                      !errors?.[name],
                  },
                )}
              >
                <ReactCurrencyInputField
                  name={field.name}
                  value={field.value}
                  placeholder="0.00"
                  prefix="$"
                  decimalSeparator="."
                  groupSeparator=","
                  decimalsLimit={2}
                  disableAbbreviations
                  allowNegativeValue={false}
                  className={clsx(
                    'w-64 border border-[#CCCCCC] rounded-md text-left text-primary-100 font-normal text-sm px-2 py-1 max-h-7 flex-1',
                    {
                      'border-[#ff7875] focus:border-[#ff7875]': errors?.[name],
                    },
                  )}
                  onValueChange={(value: any) => {
                    field.onChange(Number(value));
                  }}
                  style={{
                    border: 'none',
                    outline: 'none',
                    width: '100%',
                    backgroundColor: 'transparent',
                    fontSize: '14px',
                    color: '#000',
                  }}
                />
              </div>
            )}
          />
        </div>
        {errors?.[name] && (
          <span className="flex items-center justify-end text-right text-danger text-sm font-normal font-montserrat">
            {renderErrorMessage()}
          </span>
        )}
      </>
    );
  };

  const renderInputBoolean = () => (
    <Space
      direction="vertical"
      size={4}
      className={clsx('relative w-full my-2.5 font-montserrat', className)}
    >
      {tooltip && (
        <div className="absolute -top-5 right-0.5">
          <Tooltip title={tooltip}>
            <QuestionCircleTwoTone />
          </Tooltip>
        </div>
      )}
      <div
        className={clsx('w-full flex items-center gap-4', {
          'justify-between': spaceBetween,
        })}
      >
        <label
          htmlFor={name}
          className="font-semibold text-primary-100 text-[14px]"
        >
          {label}
        </label>
        <Controller
          control={control}
          name={name}
          render={({ field }) => (
            <Space direction="horizontal">
              <Checkbox
                {...field}
                checked={field.value}
                onChange={(e) => field.onChange(e.target.checked)}
              >
                Yes
              </Checkbox>
              <Checkbox
                {...field}
                checked={!field.value}
                onChange={(e) => field.onChange(!e.target.checked)}
              >
                No
              </Checkbox>
            </Space>
          )}
        />
      </div>
      {errors?.[name] && (
        <span className="flex items-center justify-end text-danger text-sm">
          {renderErrorMessage()}
        </span>
      )}
      {warning && (
        <span
          className={clsx(
            'flex items-center text-sm text-danger',
            warningPositions[warningPosition],
          )}
        >
          {warning}
        </span>
      )}
    </Space>
  );

  const renderInputLink = () => (
    <Space
      direction="vertical"
      size={4}
      className={clsx('relative w-full my-2.5 font-montserrat', className)}
    >
      {tooltip && (
        <div className="absolute -top-5 right-0.5">
          <Tooltip title={tooltip}>
            <QuestionCircleTwoTone />
          </Tooltip>
        </div>
      )}
      <div
        className={clsx('w-full flex items-center gap-4', {
          'justify-between': spaceBetween,
        })}
      >
        <label
          htmlFor={name}
          className="font-semibold text-primary-100 text-[14px]"
        >
          {label}
        </label>
        <div
          className={clsx('relative flex', {
            'flex-1': !spaceBetween,
          })}
        >
          <Controller
            control={control}
            name={name}
            rules={{
              ...rules,
              pattern: { value: LINK_REGEX, message: 'Invalid URL' },
            }}
            render={({ field }) => (
              <Input
                {...field}
                id={name}
                type="text"
                placeholder={label}
                disabled={disabled}
                status={errors?.[name] ? 'error' : ''}
                style={{ width: '256px' }}
              />
            )}
          />
          {children}
        </div>
      </div>
      {errors?.[name] && (
        <span className="flex items-center justify-end text-danger text-sm">
          {renderErrorMessage()}
        </span>
      )}
    </Space>
  );

  const renderInputPercentage = () => (
    <Space
      direction="vertical"
      size={4}
      className={clsx('relative w-full my-2.5 font-montserrat', className)}
    >
      {tooltip && (
        <div className="absolute -top-5 right-0.5">
          <Tooltip title={tooltip}>
            <QuestionCircleTwoTone />
          </Tooltip>
        </div>
      )}
      <div
        className={clsx('w-full flex items-center gap-4', {
          'justify-between': spaceBetween,
        })}
      >
        <div className="max-w-[12rem] flex flex-col gap-0.5">
          <label
            htmlFor={name}
            className="font-semibold text-primary-100 text-[14px]"
          >
            {label}
          </label>
          {tag && (
            <div>
              <Tag color="green">{tag}</Tag>
            </div>
          )}
        </div>
        <div
          className={clsx('relative flex', {
            'flex-1': !spaceBetween,
          })}
        >
          <Controller
            name={name}
            control={control}
            rules={{
              ...rules,
              pattern: {
                value: /^(100(\.0{1,2})?|([1-9]?[0-9])(\.[0-9]{1,2})?)$/,
                message: 'Invalid percentage',
              },
            }}
            render={({ field }) => (
              <Form.Item
                help={errors?.[name]?.message}
                validateStatus={errors?.[name] ? 'error' : ''}
                style={{ margin: 0 }}
              >
                <Input
                  id={name}
                  {...field}
                  suffix="%"
                  size="large"
                  className="text-align-right"
                />
              </Form.Item>
            )}
          />
        </div>
      </div>
    </Space>
  );

  const renderInputTime = () => (
    <Space
      direction="vertical"
      size={4}
      className={clsx('relative w-full my-2.5 font-montserrat', className)}
    >
      {tooltip && (
        <div className="absolute -top-5 right-0.5">
          <Tooltip title={tooltip}>
            <QuestionCircleTwoTone />
          </Tooltip>
        </div>
      )}
      <div
        className={clsx('w-full flex items-center gap-4', {
          'justify-between': spaceBetween,
        })}
      >
        <label
          htmlFor={name}
          className="font-semibold text-primary-100 text-[14px]"
        >
          {label}
        </label>
        <div
          className={clsx('relative flex', {
            'flex-1': !spaceBetween,
          })}
        >
          <Controller
            control={control}
            name={name}
            rules={{
              ...rules,
              validate: (value) => {
                if (value === '' || value === undefined) return true;

                // Allow strings
                if (typeof value === 'string' && isNaN(Number(value)))
                  return true;

                // Allow valid numbers
                return /^[0-9]*(\.[0-9]?)?$/.test(value)
                  ? true
                  : 'Invalid number. Must have at most one decimal.';
              },
            }}
            render={({ field }) => (
              <InputNumber
                {...field}
                id={name}
                placeholder={!disabled ? label : 'Not Defined'}
                disabled={disabled}
                status={errors?.[name] ? 'error' : ''}
                style={{ width: '256px' }}
                onChange={(value) => {
                  if (
                    value === undefined ||
                    value === '' ||
                    /^[0-9]*(\.[0-9])?$/.test(value)
                  ) {
                    field.onChange(value);
                  }
                }}
                step={0.1}
                addonAfter={decorator}
              />
            )}
          />
        </div>
      </div>
      {errors?.[name] && (
        <span className="flex items-center justify-end text-danger text-sm">
          {renderErrorMessage()}
        </span>
      )}

      {/* Custom tooltip styling */}
      <style>
        {`
          .ant-tooltip-inner {
            color: #293241;
            background-color: #f5f7fa;
            width: 320px;
          }
        `}
      </style>
    </Space>
  );

  const renderInputTierSelect = () => (
    <TierSelectField
      name={name}
      label={label}
      control={control}
      rules={rules}
      defaultValue={defaultValue}
      options={selectOptions}
      disabled={disabled}
      spaceBetween={spaceBetween}
      tooltip={tooltip}
      errors={errors}
      className={className}
    />
  );

  const renderMap = {
    [INPUT_TYPES.TEXT]: renderInputText,
    [INPUT_TYPES.NUMBER]: renderInputNumber,
    [INPUT_TYPES.EMAIL]: renderInputEmail,
    [INPUT_TYPES.DATE]: renderInputText,
    [INPUT_TYPES.SELECT]: renderInputSelect,
    [INPUT_TYPES.TEXT_AREA]: renderInputTextArea,
    [INPUT_TYPES.MONEY]: renderInputMoney,
    [INPUT_TYPES.PHONE]: renderInputText,
    [INPUT_TYPES.BOOLEAN]: renderInputBoolean,
    [INPUT_TYPES.LINK]: renderInputLink,
    [INPUT_TYPES.PERCENTAGE]: renderInputPercentage,
    [INPUT_TYPES.TIME]: renderInputTime,
    [INPUT_TYPES.TIER_SELECT]: renderInputTierSelect,
  };

  return renderMap[lowercaseFirstLetter(type)]();
};

export default InputField;
